import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

const makeWaLink = (phone, teacherName) => {
  const tFirst = (teacherName || "Sir/Ma'am").split(" ")[0];
  const msg    = `Hi ${tFirst}! 🙏 I found your profile on PadhaiPass and I need a home tutor for my child. I am interested in a free demo class. Please let me know when you are available.`;
  return `https://wa.me/91${phone.replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;
};

export default function MyTeachers() {
  const { user } = useAuth();
  const nav      = useNavigate();
  const [leads,   setLeads]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/contacts/my-contacted")
      .then(r => setLeads(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, nav]);

  const remove = async (leadId) => {
    if (!window.confirm("Remove this teacher from your list?")) return;
    setRemoving(leadId);
    try {
      await api.delete("/contacts/my-contacted/" + leadId);
      setLeads(prev => prev.filter(l => l._id !== leadId));
    } catch {
      alert("Something went wrong. Please try again.");
    }
    setRemoving(null);
  };

  return (
    <div style={{ minHeight:"100vh", background:"#060609", padding:"20px 16px 60px" }}>
      <Helmet><title>My Teachers · PadhaiPass</title></Helmet>

      <div style={{ maxWidth:600, margin:"0 auto" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
          <button onClick={() => nav(-1)}
            style={{ background:"#1e293b", border:"none", color:"#94a3b8", borderRadius:8, padding:"6px 12px", cursor:"pointer", fontSize:13 }}>
            ← Back
          </button>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:"#fff" }}>My Teachers</div>
            <div style={{ fontSize:12, color:"#475569", marginTop:2 }}>
              Teachers you've booked a demo with
            </div>
          </div>
          {leads.length > 0 && (
            <span style={{ marginLeft:"auto", background:"#f9731620", border:"1px solid #f9731640", color:"#f97316", borderRadius:20, padding:"3px 12px", fontSize:13, fontWeight:700 }}>
              {leads.length} teacher{leads.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign:"center", color:"#475569", padding:40, fontSize:14 }}>Loading...</div>
        )}

        {/* Empty */}
        {!loading && leads.length === 0 && (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🔍</div>
            <div style={{ fontSize:16, fontWeight:700, color:"#fff", marginBottom:8 }}>No demo requests yet</div>
            <div style={{ fontSize:13, color:"#475569", marginBottom:24 }}>Find a tutor and book a free demo class.</div>
            <button onClick={() => nav("/search")}
              style={{ background:"#f97316", color:"#fff", border:"none", borderRadius:10, padding:"10px 24px", fontSize:14, fontWeight:700, cursor:"pointer" }}>
              Find Tutors
            </button>
          </div>
        )}

        {/* Cards */}
        {!loading && leads.map(lead => {
          const t = lead.teacher;

          return (
            <div key={lead._id}
              style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:14, padding:"14px 16px", marginBottom:12 }}>

              <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>

                {/* Avatar */}
                <div style={{ width:52, height:52, borderRadius:"50%", background:"#1e293b", flexShrink:0, overflow:"hidden", border:"2px solid #f9731630", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {t?.photo_url
                    ? <img src={t.photo_url} alt={t?.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    : <span style={{ fontSize:22 }}>🧑‍🏫</span>
                  }
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:4 }}>
                    <span style={{ fontSize:15, fontWeight:700, color:"#f1f5f9" }}>{t?.name || "Teacher"}</span>
                    <span style={{ marginLeft:"auto", fontSize:11, fontWeight:700, borderRadius:20, padding:"2px 10px", background:"#22c55e15", color:"#22c55e", border:"1px solid #22c55e30" }}>
                      ✓ Demo Requested
                    </span>
                  </div>

                  <div style={{ fontSize:12, color:"#64748b", display:"flex", flexWrap:"wrap", gap:8, marginBottom:4 }}>
                    {t?.city && <span>📍 {t.city}{t?.area ? `, ${t.area}` : ""}</span>}
                    {lead.student_class && <span>📚 {lead.student_class}</span>}
                  </div>

                  <div style={{ fontSize:11, color:"#334155", marginBottom:10 }}>
                    {new Date(lead.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    <button onClick={() => nav(`/teacher/${t?._id}`)}
                      style={{ fontSize:12, color:"#f97316", background:"#f9731615", border:"1px solid #f9731630", borderRadius:8, padding:"6px 14px", cursor:"pointer", fontWeight:600 }}>
                      View Profile
                    </button>
                    {t?.phone && (
                      <a href={makeWaLink(t.phone, t.name)} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize:12, color:"#22c55e", background:"#22c55e15", border:"1px solid #22c55e30", borderRadius:8, padding:"6px 14px", cursor:"pointer", fontWeight:600, textDecoration:"none" }}>
                        💬 WhatsApp
                      </a>
                    )}
                    <button
                      onClick={() => remove(lead._id)}
                      disabled={removing === lead._id}
                      style={{ fontSize:12, color:"#ef4444", background:"#ef444415", border:"1px solid #ef444430", borderRadius:8, padding:"6px 14px", cursor:"pointer", fontWeight:600, marginLeft:"auto" }}>
                      {removing === lead._id ? "Removing..." : "Remove"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Footer CTA */}
        {!loading && leads.length > 0 && (
          <div style={{ textAlign:"center", marginTop:24 }}>
            <button onClick={() => nav("/search")}
              style={{ background:"transparent", color:"#64748b", border:"1px solid #1e293b", borderRadius:10, padding:"9px 20px", fontSize:13, cursor:"pointer" }}>
              + Find More Tutors
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
